import { AxiosRequestConfig } from "axios";
import Signal, { Binder, Req } from "badmfck-signal";
import { IExecutorResponse } from "badmfck-signal/dist/DataProvider";
import {
	AUTH_STATE,
	IAuth2FAConfirmRequest,
	IAuth2FASetupEmailResponse,
	IAuth2FASetupTotpResponse,
	IAuthSession,
	IAuthChangePasswordRequest,
	IAuthForm,
	IAuthVerifyTwoFactorRequest,
	TAuthLoginResult,
	TAuthPendingTwoFactor,
	TUser,
} from "./services/AuthService";
import { BalancerDispatcher } from "./services/BalancerService";
import { ICardsPagination, ICardWithAccount } from "./services/CardService";
import { IClient } from "./services/ClientService";
import type { ClientListRequest } from "./services/clientListRequest";
import { CommonDispatcher } from "./services/Common";
import { IProject } from "./services/Projects";
import { RateDispatcher } from "./services/RateService";
import { ITransaction, ITransactionLog } from "./services/Transactions";
import { IAcquirerChannel, IPaymentSession, ITerminal } from "./types/types";
import { TRegistrationForm } from "./view/Auth/RegisterView";
import { TFraudGroup, TFraudRule } from "./view/fraud/types";

// Services in main.tsx register handlers once. Replacing this registry through
// Fast Refresh would leave mounted views using requests with no handlers.
// Restart the application whenever this module (or its dependencies) updates.
if (import.meta.hot) {
	import.meta.hot.accept(() => window.location.reload());
}

export class GD {
	static RATE = RateDispatcher;
	static COMMON = CommonDispatcher;
	static BALANCER = BalancerDispatcher;

	// LAYOUT
	static B_HEADER_HEIGHT = new Binder<void, number>("B_HEADER_HEIGHT");

	// AUTHENTICATION
	static REQ_AUTH_TOKEN = new Req<void, string | null>(undefined, "REQ_AUTH_TOKEN");
	static S_AUTH_STATUS = new Signal<AUTH_STATE>("S_AUTH_STATUS");
	static S_AUTH_FORCE_PASSWORD_CHANGE = new Signal<boolean>("S_AUTH_FORCE_PASSWORD_CHANGE");
	static S_AUTH_LOG_OUT = new Signal<void>("S_AUTH_LOG_OUT");
	static S_AUTH_TOKEN_REPLACED = new Signal<string>("S_AUTH_TOKEN_REPLACED");
	static S_SESSION_TERMINATED_POPUP = new Signal<{ message: string; details?: unknown } | null>("S_SESSION_TERMINATED_POPUP");
	static REQ_AUTH_LOGIN = new Req<IAuthForm, TAuthLoginResult>(undefined, "REQ_AUTH_LOGIN");
	static REQ_AUTH_VERIFY_2FA = new Req<IAuthVerifyTwoFactorRequest, IError | null>(undefined, "REQ_AUTH_VERIFY_2FA");
	static REQ_AUTH_2FA_PENDING = new Req<void, TAuthPendingTwoFactor | null>(undefined, "REQ_AUTH_2FA_PENDING");
	static REQ_AUTH_FINGERPRINT = new Req<void, string | null>(undefined, "REQ_AUTH_FINGERPRINT");
	static REQ_AUTH_SESSIONS = new Req<void, IAPIResponse<IAuthSession[]>>(undefined, "REQ_AUTH_SESSIONS");
	static REQ_AUTH_LOGOUT_OTHERS = new Req<void, IAPIResponse<boolean>>(undefined, "REQ_AUTH_LOGOUT_OTHERS");
	static REQ_AUTH_2FA_SETUP_EMAIL = new Req<void, IAPIResponse<IAuth2FASetupEmailResponse>>(undefined, "REQ_AUTH_2FA_SETUP_EMAIL");
	static REQ_AUTH_2FA_SETUP_TOTP = new Req<void, IAPIResponse<IAuth2FASetupTotpResponse>>(undefined, "REQ_AUTH_2FA_SETUP_TOTP");
	static REQ_AUTH_2FA_CONFIRM_TOTP = new Req<IAuth2FAConfirmRequest, IAPIResponse<boolean>>(undefined, "REQ_AUTH_2FA_CONFIRM_TOTP");
	static REQ_AUTH_2FA_DISABLE = new Req<void, IAPIResponse<boolean>>(undefined, "REQ_AUTH_2FA_DISABLE");

	static S_PROFILE_POPUP = new Signal<object | null>("S_PROFILE_POPUP");

	static REQ_AUTH_REGISTER = new Req<TRegistrationForm, IAPIResponse>(undefined, "REQ_AUTH_REGISTER");
	static REQ_AUTH_CHANGE_PASSWORD = new Req<IAuthChangePasswordRequest, IAPIResponse>(undefined, "REQ_AUTH_CHANGE_PASSWORD");

	static REQ_PROFILE = new Req<void, TUser | null>(undefined, "REQ_PROFILE");
	static S_PROFILE_PATCH = new Signal<Partial<TUser>>("S_PROFILE_PATCH");
	static REG_STEP_ERROR = new Signal<{ step: string; error: IError | null }>("REG_STEP_ERROR");
	static REQ_STEP_BUSY = new Signal<boolean>("REQ_STEP_BUSY");

	static REQ_PROJECT_ID = new Req<void, number>(undefined, "REQ_PROJECT_ID");

	static S_SELECT_PROJECT = new Signal<void>("S_SELECT_PROJECT");

	static REQ_FILES_SAVE = new Req<any, IAPIResponse>();
	static REQ_FILES_LIST = new Req<{ target_id: number; target_type: string }, IAPIResponse>();
	static REQ_FILE_DELETE = new Req<{ target_id: number; target_type: string; id: string | number }, IAPIResponse>();
	static REQ_FILE_DOWNLOAD = new Req<{ target_id: number; target_type: string; id: string | number }, IAPIResponse>();

	// HTTP
	static REQ_AXIOS = new Req<AxiosRequestConfig, IHTTPResponse>(undefined, "REQ_AXIOS");
	static REQ_API = new Req<IAPIRequest, IAPIResponse>(undefined, "REQ_API");
	static S_HTTP_BUSY = new Signal<boolean>("S_HTTP_BUSY");

	// PROJECT
	static B_PROJECTS = new Binder<void, IExecutorResponse<IProject[]>>("B_PROJECTS");
	static B_SELECTED_PROJECT = new Binder<void, IProject | null>("B_PROJECT");
	static REQ_PROJECT_ID_BY_NAME = new Req<string, number>(undefined, "REQ_PROJECT_ID");
	static REQ_PROJECT_BY_ID = new Req<{ projectID: number }, IProject | null>(undefined, "REQ_PROJECT_BY_ID");
	static S_PROJECT_EDITOR = new Signal<{ projectID: number } | null>("S_PROJECT_EDITOR");
	static REQ_PROJECT_SAVE = new Req<IProject, IProject | IError | null>(undefined, "REQ_PROJECT_BY_ID");
	static S_PROJECT_SELECTED = new Signal<IProject>("S_PROJECT_SELECTED");
	static REQ_CURRENT_PROJECT = new Req<void, IProject | null>(undefined, "REQ_CURRENT_PROJECT");

	// USERS
	static REQ_USERS = new Req<
		{ excludeCurrentProject?: boolean; search?: string; clientID?: number; roles?: number[]; excludedRoles?: number[] },
		IAPIResponse<IUser[]>
	>(undefined, "REQ_USERS");
	static REQ_USER_BY_UID = new Req<string, IUser | null | undefined>(undefined, "REQ_USER_BY_UID");
	static REQ_INVITE_USER = new Req<{ email: string; role_id: number }, IAPIResponse<boolean>>(undefined, "REQ_INVITE_USER");
	static REQ_INVITE_GET = new Req<{ code: string }, IAPIResponse<IInviteUser>>(undefined, "REQ_INVITE_GET");
	static REQ_INVITES = new Req<{}, IAPIResponse<IInvite[]>>(undefined, "REQ_INVITES");

	// ACCOUNTS
	static B_ACCOUNTS = new Req<void, IExecutorResponse<IProject[]>>(undefined, "B_ACCOUNTS");
	static REQ_ACCOUNT = new Req<{ userUID: string }, IAPIResponse<IAccount>>(undefined, "REQ_ACCOUNT");
	static REQ_ACCOUNT_BY_UID = new Req<{ uid: string }, IAPIResponse<IAccount>>(undefined, "REQ_ACCOUNT_BY_UID");

	// TRANSACTIONS
	static REQ_TRANSACTIONS_BY_ACCOUNT = new Req<{ uid: string }, IAPIResponse<ITransactionPacket>>(undefined, "REQ_TRANSACTIONS_BY_USER_UID");

	static REQ_TRANSACTION_BY_UID = new Req<string, IAPIResponse<ITransaction[]>>(undefined, "REQ_TRANSACTION_BY_UID");

	static REQ_TRANSACTION_LOG = new Req<{ referenceID: string | number }, IAPIResponse<ITransactionLog[]>>(undefined, "REQ_TRANSACTION_LOG");

	static REQ_TRANSACTIONS = new Req<ITransactionFilter, IAPIResponse<ITransactionPacket>>(undefined, "REQ_TRANSACTIONS");

	static S_TRANSACTION_PREVIEW = new Signal<IPaymentSession | null>("S_TRANSACTION_PREVIEW");
	static S_TRANSACTIONS_UPDATE = new Signal<void>("S_TRANSACTIONS_UPDATE");

	// Merchants
	static REQ_MERCHANT = new Req<{ merchantID?: number; clientID?: number; groupID?: number }, IAPIResponse<IMerchantModel>>(undefined, "REQ_MERCHANT");

	static REQ_MERCHANT_SAVE = new Req<IMerchantModel, IAPIResponse<IMerchantModel>>(undefined, "REQ_MERCHANT_SAVE");

	static REQ_MERCHANT_SETTINGS_GET = new Req<{ merchantID?: number; type?: "credentials" }, IAPIResponse<IMerchantSettings>>(
		undefined,
		"REQ_MERCHANT_SETTINGS_GET",
	);

	static REQ_MERCHANT_SETTINGS_SET = new Req<{ merchantID?: number; settings: ISettingsData; id: number }, IAPIResponse<boolean>>(
		undefined,
		"REQ_MERCHANT_SETTINGS_SET",
	);

	static REQ_MERCHANT_AQUIRERS = new Req<{ merchantID?: number }, IAPIResponse<IAcquirer[]>>(undefined, "REQ_MERCHANT_AQUIRERS");

	// static REQ_MERCHANT_AQUIRER_CHANNELS = new Req<{ merchantID: number; acquirerID: number }, IAPIResponse<IAcquirer>>(
	// 	undefined,
	// 	"REQ_MERCHANT_AQUIRERS_CHANNELS"
	// );

	static REQ_MERCHANT_ADD_CHANNEL = new Req<{ merchantID: number; channelID: number }, IAPIResponse<boolean>>(undefined, "REQ_MERCHANT_ADD_CHANNEL");

	static REQ_MERCHANT_REMOVE_CHANNEL = new Req<{ merchantID: number; channelID: number }, IAPIResponse<boolean>>(undefined, "REQ_MERCHANT_REMOVE_CHANNEL");
	static REQ_MERCHANT_REMOVE = new Req<{ merchantID: number }, IAPIResponse<boolean>>(undefined, "REQ_MERCHANT_REMOVE");

	static REQ_MERCHANT_REMOVE_TERMINAL = new Req<{ merchantID: number; terminalID: number }, IAPIResponse<boolean>>(undefined, "REQ_MERCHANT_REMOVE_TERMINAL");

	static REQ_MERCHANT_RESTORE_TERMINAL = new Req<{ merchantID: number; terminalID: number[] }, IAPIResponse<boolean>>(
		undefined,
		"REQ_MERCHANT_RESTORE_TERMINAL",
	);

	static REQ_MERCHANT_ATTACHED_CHANNEL = new Req<{ merchantID: number; channelID: number }, IAPIResponse<boolean>>(undefined, "REQ_MERCHANT_ADD_CHANNEL");

	// static B_MERCHANT_ATTACHED_CHANNELS = new Binder<{ merchantID: number; terminalId?: number }, IAPIResponse<IAcquirerChannel[]>>(
	// 	"B_MERCHANT_ATTACHED_CHANNELS"
	// );

	static REQ_MERCHANT_ATTACHED_CHANNELS = new Req<{ merchantID: number; terminalId?: number }, IAPIResponse<IAcquirerChannel[]>>(
		undefined,
		"REQ_MERCHANT_ATTACHED_CHANNELS",
	);

	static B_MERCHANT_ATTACHED_TERMINALS = new Binder<{ merchantID: number }, IAPIResponse<ITerminal[]>>("B_MERCHANT_ATTACHED_TERMINALS");

	// static REQ_MERCHANT_WEBSITE = new Req<
	// 	{ websiteID: number; merchantID: number },
	// 	IAPIResponse<IMerchantWebsite>
	// >(undefined, "REQ_MERCHANT_NEW_WEBSITE");
	// static REQ_MERCHANT_PERSON = new Req<
	// 	{ personID: number; merchantID: number },
	// 	IAPIResponse<IMerchantPerson>
	// >(undefined, "REQ_MERCHANT_PERSON");
	// static REQ_MERCHANT_PARTNER = new Req<
	// 	{ partnerID: number; merchantID: number },
	// 	IAPIResponse<IMerchantPartner>
	// >(undefined, "REQ_MERCHANT_PARTNER");
	// static REQ_MERCHANT_ADDRESS = new Req<
	// 	{ addressID: number; merchantID: number },
	// 	IAPIResponse<IMerchantAddress>
	// >(undefined, "REQ_MERCHANT_ADDRESS");
	// static REQ_MERCHANT_SAVE = new Req<IMerchant, IAPIResponse<IMerchant>>(
	// 	undefined,
	// 	"REQ_MERCHANT_SAVE"
	// );
	// static REQ_ATTACH_TERMINAL = new Req<
	// 	{ merchantID: number; terminalId: number; siteId: number },
	// 	IAPIResponse<boolean>
	// >(undefined, "REQ_ATTACH_TERMINAL");

	// static REQ_MERCHANT_SET_STATUS = new Req<
	// 	{ merchantID: number; status: TMerchantStatus },
	// 	IAPIResponse<boolean>
	// >(undefined, "REQ_MERCHANT_SET_STATUS");
	// static REQ_MERCHANT_SET_AML_STATUS = new Req<
	// 	{ merchantID: number; status: TMerchantAmlStatus },
	// 	IAPIResponse<boolean>
	// >(undefined, "REQ_MERCHANT_SET_AML_STATUS");

	// CARDS
	static REQ_CARDS = new Req<ICardFilter, IAPIResponse<ICardsPagination>>(undefined, "REQ_CARDS");

	static REQ_CLIENT_FILES = new Req<{ clientID: number }, IAPIResponse<any>>(undefined, "REQ_CLIENT_FILES");

	static REQ_SETTINGS_GET = new Req<{ clientId?: number }, IAPIResponse<IClientSettings>>(undefined, "REQ_SETTINGS_GET");

	static REQ_SETTINGS_SET = new Req<{ targetId?: number; settings: ISettingsData; id: number }, IAPIResponse<boolean>>(undefined, "REQ_SETTINGS_SET");

	static REQ_CLIENT_INFO_GET = new Req<{ clientId?: number }, IAPIResponse<IInfo>>(undefined, "REQ_CLIENT_INFO_GET");

	static REQ_CLIENT_INFO_SET = new Req<{ clientID?: number; info: IInfo }, IAPIResponse<boolean>>(undefined, "REQ_CLIENT_INFO_SET");

	static REQ_CLIENT_EVENTS = new Req<{ clientID: number }, IAPIResponse<IEvent[]>>(undefined, "REQ_CLIENT_EVENTS");

	static REQ_CLIENT_USERS = new Req<{ clientID: number }, IAPIResponse<IClientUser[]>>(undefined, "REQ_CLIENT_USERS");
	static REQ_CLIENT_USER_CREATE = new Req<{ clientID: number; email: string; name?: string }, IAPIResponse<IClientUser>>(undefined, "REQ_CLIENT_USER_CREATE");
	static REQ_CLIENT_USER_BLOCK = new Req<{ clientID: number; userID: number }, IAPIResponse>(undefined, "REQ_CLIENT_USER_BLOCK");
	static REQ_CLIENT_USER_UNBLOCK = new Req<{ clientID: number; userID: number }, IAPIResponse>(undefined, "REQ_CLIENT_USER_UNBLOCK");
	static REQ_CLIENT_USER_REMOVE = new Req<{ clientID: number; userID: number }, IAPIResponse>(undefined, "REQ_CLIENT_USER_REMOVE");

	static REQ_CARD = new Req<{ uid: string }, IAPIResponse<ICardWithAccount>>(undefined, "REQ_CARD");
	static REQ_CARDS_BY_USER = new Req<{ userUID: string }, IAPIResponse<ICard[]>>(undefined, "REQ_CARDS_BY_USER");

	// MERCHANTS EDITOR
	static S_MERCHANT_EDITOR = new Signal<{ merchantID?: number } | null | undefined | void>("S_MERCHANT_EDITOR");
	static B_MERCHANTS = new Binder<{ ts: number }, IExecutorResponse<IMerchant[]>>("B_MERCHANTS");

	// WEBSITES
	static REQ_WEBSITES = new Req<{ merchantID: number }, IAPIResponse<IMerchantWebsite[]>>(undefined, "REQ_WEBSITES");

	// ACQUIRERS

	static S_ACQUIRERS_CHANGE = new Signal<{}>("S_ACQUIRERS_CHANGE");

	static B_ACQUIRERS = new Binder<{ ts: number }, IExecutorResponse<IAcquirer[]>>("B_ACQUIRERS");

	static S_ACQUIRER_EDITOR = new Signal<{ acquirerID: number } | null>("S_ACQUIRER_EDITOR");

	static REQ_ACQUIRER = new Req<{ acquirerID: number }, IAPIResponse<IAcquirer> | null>(undefined, "REQ_ACQUIRER");

	static REQ_ACQUIRER_SAVE = new Req<IAcquirer, IAPIResponse<IAcquirer>>(undefined, "REQ_ACQUIRER_SAVE");

	static REQ_ACQUIRER_ROUTE = new Req<{ routeID: number; acquirerID: number }, IAPIResponse<IAcquirerRoute>>(undefined, "REQ_ACQUIRER_ROUTE");

	static REQ_ACQUIRER_CHANNEL = new Req<{ channelId: number; acquirerID: number }, IAPIResponse<IAcquirerChannel>>(undefined, "REQ_ACQUIRER_CHANNEL");

	static REQ_MERCHANT_CHANNEL = new Req<{ channelId: number; merchantId?: number }, IAPIResponse<IAcquirerChannel>>(undefined, "REQ_MERCHANT_CHANNEL");

	// FRAUD
	static REQ_FRAUD_GROUPS_GET = new Req<unknown, TFraudGroup[]>(undefined, "REQ_FRAUD_GROUPS_GET");
	static REQ_FRAUD_GROUP_GET = new Req<{ groupID: number }, IAPIResponse<TFraudGroup> | null>(undefined, "REQ_FRAUD_GROUP_GET");
	static REQ_FRAUD_GROUP_SET = new Req<{ groupID: number; name: string }, IAPIResponse<TFraudGroup> | null>(undefined, "REQ_FRAUD_GROUPS_SET");
	static REQ_FRAUD_GROUP_DELETE = new Req<{ groupID: number }, boolean>(undefined, "REQ_FRAUD_GROUPS_DELETE");
	static REQ_FRAUD_RULES_GET = new Req<void, TFraudRule[]>(undefined, "REQ_FRAUD_RULES_GET");
	static REQ_FRAUD_RULE_GET = new Req<{ ruleID: number }, TFraudRule["data"] | null>(undefined, "REQ_FRAUD_RULE_GET");
	static REQ_FRAUD_RULE_SET = new Req<{ ruleID: number; data: TFraudRule }, TFraudRule | null>(undefined, "REQ_FRAUD_RULES_SET");
	static REQ_FRAUD_RULE_DELETE = new Req<{ ruleID: number }, boolean>(undefined, "REQ_FRAUD_RULES_DELETE");

	// MERChant website authorizations
	static REQ_MERCHANT_AUTH_CREATE = new Req<{ merchant_id: number; website_id: number }, IAPIResponse<IMerchantAuthorization>>(
		undefined,
		"REQ_MERCHANT_AUTH_CREATE",
	);
	static REQ_MERCHANT_AUTH_LIST = new Req<{ merchant_id: number; website_id: number }, IAPIResponse<IMerchantAuthorization[]>>(
		undefined,
		"REQ_MERCHANT_AUTH_LIST",
	);

	// UI
	//static S_LEFT_MENU_CLICK = new Signal<INavigationLink>("S_LEFT_MENU_CLICK");

	// WHAT, is inside акваер?
	static S_ACQUIRER_CHANNEL_EDITOR = new Signal<{
		acquirerID: number;
		channel: number;
	} | null>("S_ACQUIRER_CHANNEL_EDITOR");

	static S_ACQUIRER_ROUTE_SELECT = new Signal<{
		acquirerID: number;
		channelID: number;
	} | null>("S_ACQUIRER_ROUTE_SELECT");

	static REQ_ACQUIRER_ROUTE_SAVE = new Req<IAcquirerRoute, IAPIResponse<IAcquirerRoute>>(undefined, "REQ_ACQUIRER_ROUTE_SAVE");

	static REQ_ACQUIRER_CHANNEL_SAVE = new Req<IAcquirerChannel, IAPIResponse<IAcquirerChannel>>(undefined, "REQ_ACQUIRER_CHANNEL_SAVE");

	// TERMINALS
	static B_TERMINALS = new Binder<{ ts: number; force?: boolean }, IExecutorResponse<ITerminal[]>>("B_TERMINALS");
	static S_TERMINAL_EDITOR = new Signal<{ terminalID: number } | null>("S_TERMINAL_EDITOR");
	static S_TERMINALS_UPDATE = new Signal<void>("S_TERMINALS_UPDATE");
	static S_MERCHANT_TERMINAL_EDITOR = new Signal<{
		terminalID: number;
		websiteID: number;
		merchantID: number;
	} | null>("S_MERCHANT_TERMINAL_EDITOR");
	static REQ_TERMINAL_SAVE = new Req<ITerminal, IAPIResponse<ITerminal>>(undefined, "REQ_TERMINAL_SAVE");
	static REQ_TERMINAL = new Req<{ terminalID: number }, IAPIResponse<ITerminal> | null>(undefined, "REQ_TERMINAL");

	static REQ_MERCHANT_TERMINAL = new Req<{ terminalLinkID: number; merchantID: number }, IAPIResponse<ITerminal> | null>(undefined, "REQ_MERCHANT_TERMINAL");
	static REQ_MERCHANT_TERMINAL_ACQUIRERS_LIST = new Req<{ terminalID: number; websiteID: number; merchantID: number }, IAPIResponse<ITerminal[]> | null>(
		undefined,
		"REQ_MERCHANT_TERMINAL_ACQUIRERS_LIST",
	);
	static REQ_MERCHANT_TERMINAL_ACQUIRER_CHANNELS_LIST = new Req<
		{
			terminalID: number;
			websiteID: number;
			merchantID: number;
			acquirerID: number;
		},
		IAPIResponse<IMerchantActiveChannel[]> | null
	>(undefined, "REQ_MERCHANT_TERMINAL_ACQUIRER_CHANNELS_LIST");

	// MERChant terminals
	//static REQ_MERCHANT_AUTH_CREATE = new Req<{merchant_id:number,website_id:number},IAPIResponse<IMerchantAuthorization>>(undefined,"REQ_MERCHANT_AUTH_CREATE");

	static REQ_MERCHANT_TERMINAL_ATTACH_ACQUIRER = new Req<
		{
			merchantID: number;
			terminalID: number;
			websiteID: number;
			acquirerID: number;
		},
		IAPIResponse<IMerchantTerminalAcquirer> | null
	>(undefined, "REQ_MERCHANT_TERMINAL_ATTACH_ACQUIRER");
	static REQ_MERCHANT_TERMINAL_ATTACH_CHANNEL = new Req<
		{
			merchantID: number;
			terminalID: number;
			websiteID: number;
			acquirerID: number;
			channelID: number;
		},
		IAPIResponse<IMerchantActiveChannel> | null
	>(undefined, "REQ_MERCHANT_TERMINAL_ATTACH_CHANNEL");
	static REQ_MERCHANT_TERMINAL_LIST = new Req<{ merchant_id: number; website_id: number }, IAPIResponse<ITerminal[]>>(
		undefined,
		"REQ_MERCHANT_TERMINAL_LIST",
	);
	static REQ_MERCHANT_TERMINAL_SET_STATUS = new Req<
		{
			merchantID: number;
			terminalMerchantID: number;
			terminalID: number;
			websiteID: number;
			status: TMerchantTerminalStatus;
		},
		IAPIResponse<boolean> | null
	>(undefined, "REQ_MERCHANT_TERMINAL_SET_STATUS");
	static S_TERMINAL_ATTACH_ACQUIRER_EDITOR = new Signal<{
		onSelect: (a: IAcquirerChannel) => void;
		merchantID: number;
		terminalID: number;
		websiteID: number;
		existing?: IAcquirer[] | null;
	} | null>("S_TERMINAL_ATTACH_ACQUIRER_EDITOR");

	static S_EMULATE_TRANSFER_DIALOG = new Signal<{} | null>("S_EMULATE_TRANSFER_DIALOG");

	static S_CHECK_RECIEVER_DIALOG = new Signal<{} | null>("S_CHECK_RECIEVER_DIALOG");

	// BANKS
	static B_BANKS = new Binder<{ ts: number }, IExecutorResponse<IBank[]>>("B_ACQUIRERS");
	// static S_BANK_EDITOR = new Signal<{ REQ_BANK: number } | null>(
	// 	"S_BANK_EDITOR"
	// );
	static REQ_BANK = new Req<{ bankID: number }, IAPIResponse<IBank> | null>(undefined, "REQ_BANK");
	static REQ_BANK_SAVE = new Req<IBank, IAPIResponse<IBank>>(undefined, "REQ_BANK_SAVE");
	static REQ_BANK_DELETE = new Req<number, IAPIResponse<IBank>>(undefined, "REQ_BANK_DELETE");
	static REQ_BANK_RESTORE = new Req<number, IAPIResponse<IBank>>(undefined, "REQ_BANK_RESTORE");

	//CLIENTS
	static REQ_CLIENT = new Req<{ clientID: number }, IAPIResponse<IClient> | null>(undefined, "REQ_CLIENT");
	static REQ_CLIENTS = new Req<
		ClientListRequest,
		IAPIResponse<IClient[]> | null
	>(undefined, "REQ_CLIENTS");
	static S_CLIENTS_UPDATE = new Signal<{} | null>("S_CLIENTS_UPDATE");
	//static B_CLIENTS = new Binder<{ ts: number }, IExecutorResponse<IClient[]>>("B_CLIENTS");
	static B_CLIENT_STATUSES = new Binder<{}, IExecutorResponse<IClientStatus[]>>("B_CLIENT_STATUSES");
	static REQ_CLIENT_SAVE = new Req<IClient, IAPIResponse<IClient>>(undefined, "REQ_CLIENT_SAVE");

	static REQ_GROUP_SAVE = new Req<IClientGroup, IAPIResponse<IClientGroup>>(undefined, "REQ_GROUP_SAVE");

	static REQ_GROUP = new Req<{ groupID: number; clientID?: number }, IAPIResponse<IClientGroup> | null>(undefined, "REQ_GROUP");

	static REQ_GROUP_MERCHANTS = new Req<{ groupID: number; clientID?: number }, IAPIResponse<IMerchantModel[]> | null>(undefined, "REQ_GROUP_MERCHANTS");

	static B_GROUPS = new Binder<{ clientID?: number }, IExecutorResponse<IClientGroup[]>>("B_GROUPS");

	static S_TEST_PAYMENT = new Signal<{
		access: {
			kid: string;
			sig: string;
		}[];
		url: string;
	}>("S_TEST_PAYMENT");

	static REQ_CLIENT_STATUS_CHANGE = new Req<
		{
			client_id: number;
			status_id: number;
			reason: string;
		},
		IAPIResponse<boolean>
	>(undefined, "REQ_CLIENT_STATUS_CHANGE");

	static REQ_EVENT = new Req<
		{
			id: number;
		},
		IAPIResponse<IEvent>
	>(undefined, "REQ_EVENT");

	static S_CHANNELS_UPDATE = new Signal<{} | null>("S_CHANNELS_UPDATE");
	static REQ_CHANNELS = new Req<{ acquirerId?: number; showDeleted: boolean }, IAPIResponse<IAcquirerChannel[]> | null>(undefined, "REQ_CHANNELS");
}
